<h2 mat-dialog-title>
  {{
    (isEdit
      ? 'epd.functions.careplan-nnn.result-dialog.edit-title'
      : 'epd.functions.careplan-nnn.result-dialog.add-title'
    )
      | translate
      | capitalize
  }}
</h2>
<mat-dialog-content class="result-dialog">
  @if (!isEdit) {
    <div class="picker">
      @if (showFilterToggle()) {
        <mat-button-toggle-group
          class="picker__filter"
          hideSingleSelectionIndicator
          [value]="resultFilter()"
          (change)="onFilterChange($event.value)">
          <mat-button-toggle value="linked">
            {{ 'epd.functions.careplan-nnn.result-dialog.filter-linked' | translate | capitalize }}
          </mat-button-toggle>
          <mat-button-toggle value="all">
            {{ 'epd.functions.careplan-nnn.result-dialog.filter-all' | translate | capitalize }}
          </mat-button-toggle>
        </mat-button-toggle-group>
      }
      <mat-form-field appearance="outline" class="picker__search" [subscriptSizing]="'dynamic'">
        <input
          matInput
          type="text"
          [value]="searchText()"
          (input)="onSearchInput($event)"
          placeholder="{{ 'epd.functions.careplan-nnn.result-dialog.search-placeholder' | translate | capitalize }}" />
        <mat-icon matPrefix>search</mat-icon>
      </mat-form-field>
      <div class="progress-slot">
        @if (loadingList() || loadingTree()) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        }
      </div>
      @if (showTree()) {
        <mat-accordion class="picker__tree">
          @for (domein of nocDomeinen(); track domein.key) {
            <mat-expansion-panel
              class="tree-panel tree-panel--domein"
              [expanded]="isDomeinExpanded(domein.key)"
              (opened)="setDomeinExpanded(domein.key, true)"
              (closed)="setDomeinExpanded(domein.key, false)">
              <mat-expansion-panel-header class="tree-panel__header">
                <mat-panel-title class="tree-panel__title">
                  <span class="tree-panel__code">{{ domein.label }}</span>
                  <span class="tree-panel__label" [matTooltip]="domein.omschrijving" matTooltipClass="card-tooltip">
                    {{ domein.omschrijving }}
                  </span>
                </mat-panel-title>
              </mat-expansion-panel-header>
              @if (domein.definitie) {
                <p class="tree-panel__definitie">{{ domein.definitie }}</p>
              }
              <mat-accordion class="picker__klassen">
                @for (klasse of domein.klassen; track klasse.key) {
                  <mat-expansion-panel
                    class="tree-panel tree-panel--klasse"
                    [expanded]="isKlasseExpanded(klasse.key)"
                    (opened)="setKlasseExpanded(klasse, true)"
                    (closed)="setKlasseExpanded(klasse, false)">
                    <mat-expansion-panel-header class="tree-panel__header">
                      <mat-panel-title class="tree-panel__title">
                        <span class="tree-panel__code">{{ klasse.label }}</span>
                        <span
                          class="tree-panel__label"
                          [matTooltip]="klasse.omschrijving"
                          matTooltipClass="card-tooltip">
                          {{ klasse.omschrijving }}
                        </span>
                      </mat-panel-title>
                    </mat-expansion-panel-header>
                    @if (klasse.definitie) {
                      <p class="tree-panel__definitie">{{ klasse.definitie }}</p>
                    }
                    @if (isLoadingKlasse(klasse.key)) {
                      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                    } @else {
                      <ul class="item-list">
                        @for (zorgresultaat of itemsFor(klasse.key) ?? []; track zorgresultaat.code) {
                          <li>
                            <button
                              type="button"
                              class="item-list__item"
                              [class.item-list__item--selected]="zorgresultaat.code === selected()?.code"
                              (click)="select(zorgresultaat)">
                              <span class="item-list__code">{{ zorgresultaat.code }}</span>
                              <span class="item-list__label">{{ zorgresultaat.omschrijving }}</span>
                            </button>
                          </li>
                        } @empty {
                          <li class="item-list__empty">
                            {{ 'epd.functions.careplan-nnn.knowledge-base.no-items' | translate | capitalize }}
                          </li>
                        }
                      </ul>
                    }
                  </mat-expansion-panel>
                }
              </mat-accordion>
            </mat-expansion-panel>
          }
        </mat-accordion>
      } @else {
        <ul class="item-list picker__list">
          @for (zorgresultaat of filteredZorgresultaten(); track zorgresultaat.code) {
            <li>
              <button
                type="button"
                class="item-list__item"
                [class.item-list__item--selected]="zorgresultaat.code === selected()?.code"
                (click)="select(zorgresultaat)">
                <span class="item-list__code">{{ zorgresultaat.code }}</span>
                <span class="item-list__label">{{ zorgresultaat.omschrijving }}</span>
              </button>
            </li>
          } @empty {
            @if (!loadingList()) {
              <li class="item-list__empty">
                {{ 'epd.functions.careplan-nnn.result-dialog.no-results' | translate | capitalize }}
              </li>
            }
          }
        </ul>
      }
    </div>
  }
  <div class="indicators" [class.indicators--full-width]="isEdit">
    @if (selected(); as zorgresultaat) {
      <h3 class="indicators__title">
        <span class="indicators__code">{{ zorgresultaat.code }}</span>
        {{ zorgresultaat.omschrijving }}
      </h3>
      @if (outcomeScoreValues().length > 0) {
        <div class="scores">
          <mat-form-field appearance="outline" class="scores__field" [subscriptSizing]="'dynamic'">
            <mat-label>{{ 'epd.functions.careplan-nnn.scores.current' | translate | capitalize }}</mat-label>
            <mat-select [value]="currentScoreRef()" (valueChange)="setCurrentScore($event)">
              <mat-option [value]="null">
                {{ 'epd.functions.careplan-nnn.scores.none' | translate | capitalize }}
              </mat-option>
              @for (score of outcomeScoreValues(); track score.id) {
                <mat-option [value]="score.id"
                  >{{ score.meetwaardeSchaal }} &mdash; {{ score.omschrijving }}</mat-option
                >
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="scores__field" [subscriptSizing]="'dynamic'">
            <mat-label>{{ 'epd.functions.careplan-nnn.scores.desired' | translate | capitalize }}</mat-label>
            <mat-select [value]="desiredScoreRef()" (valueChange)="setDesiredScore($event)">
              <mat-option [value]="null">
                {{ 'epd.functions.careplan-nnn.scores.none' | translate | capitalize }}
              </mat-option>
              @for (score of outcomeScoreValues(); track score.id) {
                <mat-option [value]="score.id"
                  >{{ score.meetwaardeSchaal }} &mdash; {{ score.omschrijving }}</mat-option
                >
              }
            </mat-select>
          </mat-form-field>
        </div>
      }
      <span class="indicators__subtitle">
        {{ 'epd.functions.careplan-nnn.result-dialog.indicators' | translate | capitalize }}
      </span>
      <div class="progress-slot">
        @if (loadingIndicators()) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        }
      </div>
      <ul class="indicators__list">
        @for (indicator of indicators(); track indicator.code) {
          <li class="indicators__item" [class.indicators__item--selected]="isChecked(indicator)">
            <mat-checkbox [checked]="isChecked(indicator)" (change)="toggleIndicator(indicator)" color="primary">
              {{ indicator.omschrijving }}
            </mat-checkbox>
            @if (isChecked(indicator) && indicatorScoreValues(indicator).length > 0) {
              <div class="indicators__scores">
                <mat-form-field appearance="outline" class="indicators__score" [subscriptSizing]="'dynamic'">
                  <mat-label>{{ 'epd.functions.careplan-nnn.scores.current' | translate | capitalize }}</mat-label>
                  <mat-select [value]="indicatorScore(indicator)" (valueChange)="setIndicatorScore(indicator, $event)">
                    <mat-option [value]="null">
                      {{ 'epd.functions.careplan-nnn.scores.none' | translate | capitalize }}
                    </mat-option>
                    @for (score of indicatorScoreValues(indicator); track score.id) {
                      <mat-option [value]="score.id">
                        {{ score.meetwaardeSchaal }} &mdash; {{ score.omschrijving }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="indicators__score" [subscriptSizing]="'dynamic'">
                  <mat-label>{{ 'epd.functions.careplan-nnn.scores.desired' | translate | capitalize }}</mat-label>
                  <mat-select
                    [value]="indicatorDesiredScore(indicator)"
                    (valueChange)="setIndicatorDesiredScore(indicator, $event)">
                    <mat-option [value]="null">
                      {{ 'epd.functions.careplan-nnn.scores.none' | translate | capitalize }}
                    </mat-option>
                    @for (score of indicatorScoreValues(indicator); track score.id) {
                      <mat-option [value]="score.id">
                        {{ score.meetwaardeSchaal }} &mdash; {{ score.omschrijving }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
            }
          </li>
        } @empty {
          @if (!loadingIndicators()) {
            <li class="indicators__empty">
              {{ 'epd.functions.careplan-nnn.result-dialog.no-indicators' | translate | capitalize }}
            </li>
          }
        }
      </ul>
    } @else {
      <p class="indicators__empty">
        {{ 'epd.functions.careplan-nnn.result-dialog.pick-first' | translate | capitalize }}
      </p>
    }
  </div>
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button mat-stroked-button mat-dialog-close>{{ 'general.words.cancel' | translate | capitalize }}</button>
  <button mat-flat-button color="primary" [disabled]="!selected()" [mat-dialog-close]="buildResult()">
    {{ (isEdit ? 'general.actions.save' : 'general.actions.add') | translate | capitalize }}
  </button>
</mat-dialog-actions>
